import { useEffect, useRef } from 'react';
import { useGenericBi } from './use-generic-bi';
import type { MessageChoices } from './message-choices';

/**
 * Reports that the assistant offered a set of choice chips.
 *
 * The tap has been instrumented on all three surfaces for a while; the offer had not, so a chip that
 * nobody pressed was indistinguishable from one that was never made — and the take-up rate of an offer
 * has no denominator without this.
 *
 * `conversation_id` + `message_id` carry it, and NOT the labels. The labels are model-authored from the
 * caller's own context and routinely name their contacts and clients ("Reply to Barbara", "Set up
 * WhatsApp Business for Dive Bar"), so shipping them would move third-party personal names into the
 * analytics warehouse as a side effect of counting an event. `message_id` reaches the same text through
 * the message row for anyone who is entitled to read it, which is the property that makes withholding
 * it free.
 *
 * WEB ONLY, by decision — so any figure read off this event covers web traffic alone, and a reader
 * comparing it against the tap (which all three surfaces report) is comparing different populations.
 * If it is ever ported, the rule below is the part that does not copy across: firing as the chips
 * render counts every ask in a reopened transcript, so a phone doing the obvious thing would disagree
 * with this number quietly rather than break visibly. The seed has to be anchored in each platform's
 * own chat view model.
 */
const REGION = 'chat-choices-shown';

type ChoiceMessage = { id?: string; finalId?: string; choices?: MessageChoices };

export function useChoicesShownBi(
  messages: readonly ChoiceMessage[],
  conversationId: string | null | undefined,
  componentParentName: string,
) {
  const { logLoadFinish } = useGenericBi(componentParentName);
  const reported = useRef(new Set<string>());
  const seededFor = useRef<string | null | undefined>(undefined);
  const seeded = useRef(false);

  useEffect(() => {
    const asks = messages.filter((m): m is ChoiceMessage & { choices: MessageChoices } => !!m.choices);

    // A transcript is not a series of offers being made. Opening a conversation renders every ask it
    // already contains, so reporting on render would count each of a user's past offers again every
    // time they came back to read the thread. Seed from what is already there and report only what
    // arrives after — re-seeded per conversation, or switching threads would replay the new one's
    // history as if it had just been offered.
    if (!seeded.current || seededFor.current !== conversationId) {
      seeded.current = true;
      seededFor.current = conversationId;
      reported.current = new Set(asks.map((m) => m.choices.askId));
      return;
    }

    for (const message of asks) {
      const { askId } = message.choices;
      if (reported.current.has(askId)) continue;
      reported.current.add(askId);
      logLoadFinish(
        REGION,
        { isSuccessful: true },
        {
          additionalFields: {
            ...(conversationId ? { conversation_id: conversationId } : {}),
            // The persisted id: a bubble rendered live over SSE carries it as `finalId`, one read back
            // from history as `id`. Without it the event cannot be joined to what was offered.
            ...(message.finalId ?? message.id ? { message_id: message.finalId ?? message.id! } : {}),
          },
        },
      );
    }
  }, [messages, conversationId, logLoadFinish]);
}
